// FormBuilderPage.blop - Demonstrates type-safe forms with real-time validation

def FormField(ctx: Component): VNode {
  { attributes } = ctx
  { label, type, value, error, onChange } = attributes

  hasError = error && error.length > 0
  borderColor = if hasError => '#dc3545' else '#dee2e6'

  <div class="mb-3">
    <label class="form-label" style={ fontWeight: 'bold', marginBottom: '0.5em' }>
      = label
    </label>
    <input
      type=type
      class="form-control"
      value=value
      style={ borderColor, borderWidth: '1px' }
      on={ input: onChange }
      placeholder="Enter label" />
    if hasError {
      <small class="form-text"
        style={ color: '#dc3545',
          display: 'block', marginTop: '0.25em'
      }>error</small>
    }
  </div>
}

def validateEmail(email: string): string {
  if !email {
    return 'Email is required'
  }
  if !email.includes('@') {
    return 'Invalid email format'
  }
  return ''
}

def validatePassword(password: string): string {
  if !password {
    return 'Password is required'
  }
  if password.length < 8 {
    return 'Password must be at least 8 characters'
  }
  return ''
}

def validateName(name: string): string {
  if !name {
    return 'Full name is required'
  }
  if name.length < 2 {
    return 'Name must be at least 2 characters'
  }
  return ''
}

def getPasswordStrength(password: string): string {
  if !password || password.length == 0 {
    return ''
  }
  if password.length < 6 {
    return 'weak'
  }
  if password.length >= 12 {
    return 'strong'
  }
  return 'medium'
}

def getStrengthColor(strength: string): string {
  if strength == 'strong' {
    return '#28a745'
  }
  if strength == 'medium' {
    return '#ffc107'
  }
  return '#dc3545'
}

def getStrengthWidth(strength: string): string {
  if strength == 'strong' {
    return '100%'
  }
  if strength == 'medium' {
    return '60%'
  }
  return '30%'
}

def PasswordField(ctx): VNode {
  { attributes } = ctx
  { label, value, error, onChange, showPassword, onToggleShow } = attributes

  hasError = error && error.length > 0
  borderColor = if hasError => '#dc3545' else '#dee2e6'
  inputType = if showPassword => 'text' else 'password'

  <div class="mb-3">
    <label class="form-label" style={ fontWeight: 'bold', marginBottom: '0.5em' }>
      = label
    </label>
    <div style={ position: 'relative', display: 'flex', alignItems: 'center' }>
      <input
        type=inputType
        class="form-control"
        value=value
        style={ borderColor, borderWidth: '1px', paddingRight: '3.5em' }
        on={ input: onChange } />
      <button
        type="button"
        style={ position: 'absolute', right: '0.6em', background: 'none', border: 'none', cursor: 'pointer',
          color: '#6c757d', fontSize: '0.78em', padding: '0', lineHeight: '1' }
        on={ click: onToggleShow }>
        = if showPassword => 'Hide' else 'Show'
      </button>
    </div>
    if hasError {
      <small class="form-text" style={ color: '#dc3545', display: 'block', marginTop: '0.25em' }>
        = error
      </small>
    }
  </div>
}

FormBuilderPage = (ctx: Component): VNode => {
  { value as formData, setState as setFormData } = ctx.state<{ name: string, email: string,
    password: string, confirmPassword: string }>('formData', {
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  })

  { value as touched, setState as setTouched } = ctx.state<object>('touched', { name: false, email: false,
    password: false, confirmPassword: false })
  { value as submitted, setState as setSubmitted } = ctx.state<boolean>('submitted', false)
  { value as isLoading, setState as setIsLoading } = ctx.state<boolean>('isLoading', false)
  { value as success, setState as setSuccess } = ctx.state<boolean>('success', false)
  { value as showPassword, setState as setShowPassword } = ctx.state<boolean>('showPassword', false)

  def updateField(fieldName: string) {
    return (e) => {
      newData = { ...formData }
      newData[fieldName] = e.target.value
      setFormData(newData)
    }
  }

  def markTouched(fieldName: string) {
    return () => {
      newTouched = { ...touched }
      newTouched[fieldName] = true
      setTouched(newTouched)
    }
  }

  def getFieldError(fieldName: string): string | null {
    if !touched[fieldName] && !submitted {
      return null
    }
    if fieldName == 'name' {
      return validateName(formData.name)
    }
    if fieldName == 'email' {
      return validateEmail(formData.email)
    }
    if fieldName == 'password' {
      return validatePassword(formData.password)
    }
    if fieldName == 'confirmPassword' && !formData.confirmPassword {
      return 'Please confirm password'
    }
    if formData.confirmPassword != formData.password {
      return 'Passwords do not match'
    }
    return null
  }

  def isFormValid(): boolean {
    nameErr = validateName(formData.name)
    emailErr = validateEmail(formData.email)
    passwordErr = validatePassword(formData.password)
    confirmErr = if formData.confirmPassword != formData.password => 'Mismatch' else null

    return !nameErr && !emailErr && !passwordErr && !confirmErr
  }

  def handleSubmit(e) {
    e.preventDefault()
    setSubmitted(true)
    setSuccess(false)

    if isFormValid() {
      setIsLoading(true)
      // Simulate API call
      setTimeout(() => {
        setIsLoading(false)
        setSuccess(true)
        console.log('Form submitted:', formData)
        // Reset form
        setFormData({ name: '', email: '', password: '', confirmPassword: '' })
        setTouched({ name: false, email: false, password: false, confirmPassword: false })
        setSubmitted(false)
      }, 1500)
    }
  }

  <div class="container" style={ maxWidth: '500px', marginTop: '3em', marginBottom: '3em' }>
    <div class="card">
      <div class="card-header" style={ backgroundColor: '#667eea', color: 'white', fontWeight: 'bold', fontSize: '1.1em'
      }>
        '📋 User Registration Form'
      </div>
      <div class="card-body" style={ padding: '2em' }>
        <p style={ color: '#6c757d', marginBottom: '1.5em', fontSize: '0.95em' }>
          'This form demonstrates:'
        </p>
        <ul style={ color: '#6c757d', marginBottom: '2em', fontSize: '0.9em' }>
          <li>'Type-safe form state management'</li>
          <li>'Real-time field validation'</li>
          <li>'Password strength indicator'</li>
          <li>'Show/hide password toggle'</li>
          <li>'Dependent field validation'</li>
        </ul>

        <form on={ submit: handleSubmit }>
          <FormField label='Full Name' type='text'
            value=formData.name error=getFieldError('name') onChange=updateField('name') />

          <FormField label='Email Address' type='email'
            value=formData.email error=getFieldError('email') onChange=updateField('email') />

          <PasswordField label='Password' value=formData.password
            error=getFieldError('password') onChange=updateField('password')
            showPassword=showPassword onToggleShow=() => setShowPassword(!showPassword) />

          passwordStrength = getPasswordStrength(formData.password)
          if passwordStrength {
            strengthColor = getStrengthColor(passwordStrength)
            strengthWidth = getStrengthWidth(passwordStrength)
            <div style={ marginTop: '-0.5em', marginBottom: '1em' }>
              <div style={ height: '4px', backgroundColor: '#e9ecef', borderRadius: '2px', overflow: 'hidden' }>
                <div style={ height: '100%', width: strengthWidth, backgroundColor: strengthColor }></div>
              </div>
              <small style={ color: strengthColor, fontSize: '0.75em', marginTop: '0.2em', display: 'block' }>
                = 'Strength: ' + passwordStrength
              </small>
            </div>
          }

          <PasswordField label='Confirm Password' value=formData.confirmPassword
            error=getFieldError('confirmPassword') onChange=updateField('confirmPassword')
            showPassword=showPassword onToggleShow=() => setShowPassword(!showPassword) />

          <button
            type="submit"
            class="btn btn-primary w-100"
            style={ marginTop: '1em' }
            disabled=isLoading>
            if isLoading {
              <span>'Creating account...'</span>
            } else {
              <span>'Register'</span>
            }
          </button>
        </form>

        if success {
          <div class="alert alert-success" style={ marginTop: '1em' }>
            '✓ Account created successfully!'
          </div>
        }
      </div>
    </div>
  </div>
}
